import React, { useEffect, useState } from 'react';
import { loadPlausible } from '../lib/analytics';

const STORAGE_KEY = 'vp_cookie_consent_v1';

/**
 * POPIA-friendly cookie consent. Shows on first visit; remembers choice in
 * localStorage. Default for "close without choosing" = deny non-essential.
 *
 * Categories:
 *   essential — always on (required for the app to function)
 *   analytics — anonymised usage (Plausible/etc; not yet wired)
 *   marketing — retargeting / external pixels (not yet wired)
 *
 * Other components can read the choice via:
 *   const c = JSON.parse(localStorage.getItem('vp_cookie_consent_v1') || 'null');
 */
const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) {
        // Slight delay so it doesn't fight the page load animation
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  const persist = (choice) => {
    const record = {
      ...choice,
      essential: true,
      decided_at: new Date().toISOString(),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch {}
    setOpen(false);
    // If user opted in to analytics, load Plausible immediately
    if (choice.analytics) {
      try { loadPlausible(); } catch {}
    }
  };

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const denyAll = () => persist({ analytics: false, marketing: false });
  const saveCustom = () => persist({ analytics: !!prefs.analytics, marketing: !!prefs.marketing });

  if (!open) return null;

  return (
    <div
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto bg-[#1F3D2B] text-white rounded-2xl shadow-2xl pointer-events-auto overflow-hidden">
        {!showPrefs ? (
          <div className="p-5 sm:p-6">
            <p className="font-serif text-lg leading-snug mb-1">A note about cookies</p>
            <p className="text-sm text-white/75 leading-relaxed mb-4">
              We use essential cookies to make VinePass work. With your permission, we'd like to use anonymised analytics to improve the experience. You can change this anytime.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 bg-white text-[#1F3D2B] rounded-full py-2.5 text-sm font-semibold hover:bg-stone-100 transition-colors"
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={denyAll}
                data-testid="cookie-deny"
                className="flex-1 bg-transparent border border-white/30 text-white rounded-full py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Deny non-essential
              </button>
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-preferences"
                className="flex-1 bg-transparent text-white rounded-full py-2.5 text-sm hover:underline"
              >
                Preferences
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-6">
            <p className="font-serif text-lg leading-snug mb-3">Cookie preferences</p>
            <div className="space-y-3 mb-4">
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked readOnly className="mt-1" />
                <div>
                  <p className="font-semibold">Essential <span className="text-xs text-white/60 font-normal">(always on)</span></p>
                  <p className="text-xs text-white/60">Required for login, checkout, and security. Can't be disabled.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  className="mt-1"
                  data-testid="cookie-pref-analytics"
                />
                <div>
                  <p className="font-semibold">Analytics</p>
                  <p className="text-xs text-white/60">Anonymised usage stats so we can improve VinePass.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                  className="mt-1"
                  data-testid="cookie-pref-marketing"
                />
                <div>
                  <p className="font-semibold">Marketing</p>
                  <p className="text-xs text-white/60">Retargeting pixels for upcoming experiences.</p>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                className="text-sm px-4 py-2 hover:underline"
              >
                Back
              </button>
              <button
                type="button"
                onClick={saveCustom}
                data-testid="cookie-save-prefs"
                className="bg-white text-[#1F3D2B] rounded-full py-2 px-5 text-sm font-semibold hover:bg-stone-100 transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
